// Submit-time chokepoint: convert a fhirpath-patch `Parameters` resource into
// concrete JSON-patch operations against a base resource. Part of the
// fhirpath-patch delete boundary — see
// ./DELETE-WHEN-MEDPLUM-SHIPS-TRANSPORT-FHIRPATH.md.
//
// This does NOT translate FHIRPath to JSON pointers textually. It applies the
// fhirpath patch to a deep clone of the base resource via the vendored
// Medplum applier (true `where(...)` evaluation), then diffs before → after
// with `rfc6902.createPatch` to emit pointer ops — sidestepping
// `where(...)`/index translation fragility entirely.

import { deepClone, toTypedValue } from '@medplum/core'
import type { Parameters, Resource } from '@medplum/fhirtypes'
import { createPatch, type Operation } from 'rfc6902'
import { parametersToFhirPathPatchOperations } from '#src/fhirpath-patch/parameters-codec.ts'
import type { FhirPathPatchOperation } from '#src/fhirpath-patch/types.ts'
import {
  type FhirPathPatch,
  fhirpathPatchTypedValue,
} from '#src/fhirpath-patch/vendor/fhirpath-patch-apply.ts'
import type { JsonLike, JsonObject } from '#src/json.ts'

// Thrown when the fhirpath patch cannot be applied to the base resource
// (path resolves to nothing/multiple elements, malformed operation, …).
// Callers surface this instead of rendering or submitting a wrong patch.
export class FhirPathPatchApplyError extends Error {
  constructor(message: string, cause?: unknown) {
    super(message)
    this.name = 'FhirPathPatchApplyError'
    this.cause = cause
  }
}

export interface FhirPathPatchToJsonPatchOptions {
  // When provided, a `test /meta/versionId` precondition is prepended so the
  // server rejects the patch if the resource moved past this version.
  versionId?: string
}

export function fhirPathPatchToJsonPatch(
  parameters: Parameters,
  resource: Resource,
  options?: FhirPathPatchToJsonPatchOptions
): Operation[] {
  const operations = parametersToFhirPathPatchOperations(parameters)
  const revised = applyFhirPathPatch(resource, operations)

  const patch = createPatch(resource, revised)
  if (options?.versionId) {
    return [
      { op: 'test', path: '/meta/versionId', value: options.versionId },
      ...patch,
    ]
  }
  return patch
}

// Apply fhirpath-patch operations to a deep clone of `resource` and return
// the revised resource. Throws `FhirPathPatchApplyError` on failure.
export function applyFhirPathPatch(
  resource: Resource,
  operations: readonly FhirPathPatchOperation[]
): Resource {
  const revised = deepClone(resource)
  try {
    fhirpathPatchTypedValue(toTypedValue(revised), operations.map(toVendorOp))
  } catch (error) {
    throw new FhirPathPatchApplyError(
      `Failed to apply fhirpath patch to ${resource.resourceType}/${resource.id}: ${
        error instanceof Error ? error.message : String(error)
      }`,
      error
    )
  }
  // fhirpath `delete` removes elements but leaves emptied arrays behind;
  // FHIR JSON forbids empty arrays, so prune them to match what a whole-field
  // JSON-patch remove would have produced.
  pruneEmptyArrays(revised)
  return revised
}

function toVendorOp(op: FhirPathPatchOperation): FhirPathPatch {
  switch (op.type) {
    case 'add':
      if (op.name === undefined || op.value === undefined) {
        throw new FhirPathPatchApplyError(
          `fhirpath add operation at '${op.path}' is missing name or value`
        )
      }
      return { type: 'add', path: op.path, name: op.name, value: op.value }
    case 'insert':
      if (op.index === undefined || op.value === undefined) {
        throw new FhirPathPatchApplyError(
          `fhirpath insert operation at '${op.path}' is missing index or value`
        )
      }
      return { type: 'insert', path: op.path, index: op.index, value: op.value }
    case 'replace':
      if (op.value === undefined) {
        throw new FhirPathPatchApplyError(
          `fhirpath replace operation at '${op.path}' is missing value`
        )
      }
      return { type: 'replace', path: op.path, value: op.value }
    case 'delete':
      return { type: 'delete', path: op.path }
    case 'move':
      if (op.source === undefined || op.destination === undefined) {
        throw new FhirPathPatchApplyError(
          `fhirpath move operation at '${op.path}' is missing source or destination`
        )
      }
      return {
        type: 'move',
        path: op.path,
        source: op.source,
        destination: op.destination,
      }
    default:
      throw new FhirPathPatchApplyError(
        `Unknown fhirpath patch operation type '${op.type satisfies never}'`
      )
  }
}

function pruneEmptyArrays(node: JsonLike | undefined): void {
  if (Array.isArray(node)) {
    for (const item of node) {
      pruneEmptyArrays(item)
    }
    return
  }
  if (node && typeof node === 'object') {
    for (const [key, value] of Object.entries(node)) {
      if (Array.isArray(value) && value.length === 0) {
        delete (node as JsonObject)[key]
      } else {
        pruneEmptyArrays(value)
      }
    }
  }
}
